import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { PermissionDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { NewEmailAiSuggestionButtonDialog } from '@/components/emailWindow/components/newEmail/components/aiSuggestionButton/components/dialog/NewEmailAiSuggestionButtonDialog.comp.tsx';
import { useHasAnyPermission } from '@/utils/usePermissions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useNewEmailSetAutosavePaused } from '@/views/emails/context/NewEmails.ts';

type Props = {
  emailId: string | undefined;
  isDisabled: boolean;
};

export const NewEmailAiSuggestionButton: React.FC<Props> = ({ emailId, isDisabled }) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body');
  const setAutosavePaused = useNewEmailSetAutosavePaused();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const canUseAiSuggestions = useHasAnyPermission(PermissionDto.UseEmailAiSuggestions);

  if (!canUseAiSuggestions) {
    return null;
  }

  if (!emailId) {
    return null;
  }

  const handleClick = () => {
    setAutosavePaused(true);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = async (refetch: boolean) => {
    if (refetch) {
      await queryClient.refetchQueries({ queryKey: [QueryKeyEnum.EMAILS.READ, emailId] });
    }
    setAutosavePaused(false);
    setIsDialogOpen(false);
  };

  return (
    <>
      <Tooltip disableHoverListener={!isDisabled} title={t('tooltip.aiSuggestion')}>
        <span>
          <Button
            disabled={isDisabled}
            endIcon={<AutoAwesomeOutlinedIcon />}
            onClick={handleClick}
            size="small"
            variant="outlined"
          >
            {t('button.aiSuggestion')}
          </Button>
        </span>
      </Tooltip>
      {isDialogOpen && (
        <NewEmailAiSuggestionButtonDialog emailId={emailId} onClose={handleCloseDialog} />
      )}
    </>
  );
};
